import dayjs from 'dayjs';
import classNames from 'classnames';

import { ForecastHourDetail } from 'hooks/useGetForecast';
import { useSettingsContext } from 'providers/SettingsProvider';
import { UnitDisplay, Units, UnitsOfMeasurement } from 'utils/constants';
import Icon, { Icons } from 'components/common/icons/Icon';
import Integer from 'components/common/text/Integer';
import Decimal from 'components/common/text/Decimal';

type ForecastHourProps = {
  className?: string;
  hour: ForecastHourDetail;
};

function ForecastHour({ className, hour }: ForecastHourProps) {
  const settings = useSettingsContext();
  const isMetric = UnitsOfMeasurement.Metric === settings.units;
  const hasPrecipitation = (isMetric && hour.precip_mm) || (!isMetric && hour.precip_in);

  const hourOfDay = dayjs(hour.time);
  return (
    <div data-testid="forecast-hour" className={classNames(className, 'mb-2 snap-start')}>
      <div className="flex w-16 flex-col items-center justify-between text-sm">
        <img
          className="mb-1"
          src={`https:${hour.condition.icon}`}
          alt={hour.condition.text}
          height="48"
          width="48"
        />
        <div className="mb-1">
          <Integer
            value={isMetric ? hour.temp_c : hour.temp_f}
            unit={isMetric ? Units.Celsius : Units.Fahrenheit}
          />
        </div>
        <div className="mb-1">
          <Integer value={hour.chance_of_rain} unit={Units.Percent} />
          <Icon name={Icons.Droplet} size="xs" className="ml-1 text-blue-600/50" />
        </div>
        <div className="mb-1">
          {hasPrecipitation ? (
            <Decimal
              value={isMetric ? hour.precip_mm : hour.precip_in}
              unit={isMetric ? Units.Millimeter : Units.Inch}
              unitDisplay={UnitDisplay.Narrow}
              maximumFractionDigits={isMetric ? 1 : 2}
            />
          ) : (
            '-'
          )}
        </div>
        <div className="mb-1">
          <Integer
            value={isMetric ? hour.wind_kph : hour.wind_mph}
            unit={isMetric ? Units.KilometerPerHour : Units.MilePerHour}
            unitDisplay={UnitDisplay.Narrow}
          />
        </div>
        <div className="font-bold">{hourOfDay.format('hA')}</div>
      </div>
    </div>
  );
}

export default ForecastHour;
